import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaXmark, FaEye, FaDownload, FaFileLines } from 'react-icons/fa6';
import Preloader from './Preloader';

// Add CSS animation for loading spinner
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('style[data-spinner]')) {
  spinnerStyle.setAttribute('data-spinner', 'true');
  document.head.appendChild(spinnerStyle);
}

function ResponderApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // Changed from 'pending' to 'all' to show all applications by default
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  
  const itemsPerPage = 25; // Increased from 10 to show more applications per page
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Responder Applications";
    fetchApplications();
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchApplications();
    }
  }, [currentUser, search, statusFilter]);

  async function getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      setCurrentUser(profile);
    }
  }

  async function fetchApplications() {
    if (!currentUser) return;
    
    setLoading(true);
    setError('');
    
    try {
      let query = supabase
        .from('responder_applications')
        .select('*');

      if (search) {
        query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%,profession.ilike.%${search}%,organization.ilike.%${search}%`);
      }

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching applications:', error);
        setError('Failed to load applications');
        setApplications([]);
      } else {
        setApplications(data || []);
      }
    } catch (error) {
      console.error('Error in fetchApplications:', error);
      setError('Failed to load applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleApplicationAction(application, action) {
    setActionLoading(true);
    
    try {
      // Use the profile ID directly since we know it exists
      const { error: rpcError } = await supabase.rpc('update_responder_application_status_simple', {
        application_id: application.id,
        new_status: action,
        reviewer_user_id: currentUser.user_id,  // Use the user_id from currentUser profile
        admin_notes: adminNotes
      });

      if (rpcError) {
        // Fallback to direct update
        const updateData = {
          status: action,
          reviewed_by: currentUser.id,
          reviewed_at: new Date().toISOString(),
          admin_notes: adminNotes
        };

        const { error: updateError } = await supabase
          .from('responder_applications')
          .update(updateData)
          .eq('id', application.id);

        if (updateError) throw updateError;
      }

      // If approved, create record in approved_responders table
      if (action === 'approved') {
        const approvedResponderData = {
          user_id: application.user_id,
          application_id: application.id,
          first_name: application.first_name,
          last_name: application.last_name,
          mobile_number: application.mobile_number,
          email: application.email,
          address: application.address,
          barangay: application.barangay,
          city: application.city,
          profession: application.profession,
          organization: application.organization,
          experience_years: application.experience_years,
          certifications: application.certifications,
          is_active: true,
          is_available: true,
          approved_at: new Date().toISOString()
        };

        const { error: insertError } = await supabase
          .from('approved_responders')
          .insert([approvedResponderData])
          .select(); // return row so DB default responder_id is generated

        if (insertError) throw insertError;
      }

      // Log audit action
      await supabase.from('audit_logs').insert([
        {
          user_id: currentUser.id,
          action: `${action} responder application`,
          target_id: application.id,
          target_type: 'ResponderApplication',
          details: { 
            applicant_email: application.email,
            applicant_name: `${application.first_name} ${application.last_name}`,
            admin_notes: adminNotes
          }
        }
      ]);

      setShowModal(false);
      setSelectedApplication(null);
      setAdminNotes('');
      fetchApplications();
      
          } catch (error) {
        console.error('Error updating application:', error);
        setError('Failed to update application');
      } finally {
        setActionLoading(false);
      }
  }

  // Filter applications
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      const matchesSearch = search.trim() === '' ||
        (app.first_name && app.first_name.toLowerCase().includes(search.toLowerCase())) ||
        (app.last_name && app.last_name.toLowerCase().includes(search.toLowerCase())) ||
        (app.email && app.email.toLowerCase().includes(search.toLowerCase())) ||
        (app.profession && app.profession.toLowerCase().includes(search.toLowerCase())) ||
        (app.organization && app.organization.toLowerCase().includes(search.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [applications, search, statusFilter]);

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginatedApplications = useMemo(() => {
    return filteredApplications.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [filteredApplications, page, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  function getStatusColor(status) {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      default: return '#9e9e9e';
    }
  }

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  async function handleDocumentPreview(document) {
    console.log('=== DOCUMENT PREVIEW DEBUG ===');
    console.log('Original URL:', document.url);
    console.log('Document type:', document.type);
    console.log('Document name:', document.name);
    
    // Enhanced image detection logic
    const isImageFile = (url, type, name) => {
      // Check by document type field (from database)
      if (type && typeof type === 'string') {
        const typeCheck = type.toLowerCase();
        if (typeCheck.includes('image') || 
            typeCheck.includes('jpg') || 
            typeCheck.includes('jpeg') || 
            typeCheck.includes('png') || 
            typeCheck.includes('gif') || 
            typeCheck.includes('bmp') || 
            typeCheck.includes('webp') || 
            typeCheck.includes('svg')) {
          console.log('✅ Detected as image by type field:', type);
          return true;
        }
      }
      
      // Check by file name
      if (name && typeof name === 'string') {
        const nameCheck = name.toLowerCase();
        if (nameCheck.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)) {
          console.log('✅ Detected as image by name:', name);
          return true;
        }
      }
      
      // Check by URL (extract extension before query parameters)
      if (url && typeof url === 'string') {
        try {
          const urlObj = new URL(url);
          const pathname = urlObj.pathname.toLowerCase();
          if (pathname.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)) {
            console.log('✅ Detected as image by URL pathname:', pathname);
            return true;
          }
          
          // Also check the full URL as fallback
          if (url.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i)) {
            console.log('✅ Detected as image by URL pattern:', url);
            return true;
          }
        } catch (e) {
          // If URL parsing fails, check the raw string
          if (url.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i)) {
            console.log('✅ Detected as image by URL fallback:', url);
            return true;
          }
        }
      }
      
      console.log('❌ Not detected as image file');
      return false;
    };
    
    // Store image detection result
    let isImage = isImageFile(document.url, document.type, document.name);
    console.log('Initial image detection result:', isImage);
    
    // If not detected as image, try to check MIME type via HEAD request
    if (!isImage && document.url) {
      try {
        console.log('🔍 Making HEAD request to check MIME type...');
        const headResponse = await fetch(document.url, { 
          method: 'HEAD',
          mode: 'cors'
        });
        
        if (headResponse.ok) {
          const contentType = headResponse.headers.get('content-type');
          console.log('Content-Type from server:', contentType);
          
          if (contentType && contentType.toLowerCase().startsWith('image/')) {
            console.log('✅ Detected as image via MIME type:', contentType);
            isImage = true;
          }
        }
      } catch (e) {
        console.warn('Could not check MIME type:', e.message);
      }
    }
    
    console.log('Final image detection result:', isImage);
    
    let formattedUrl = document.url;
    
    // Enhanced URL handling for Supabase storage
    if (formattedUrl && formattedUrl.includes('supabase.co')) {
      try {
        // Multiple approaches to get the correct URL
        const url = new URL(formattedUrl);
        const pathParts = url.pathname.split('/');
        
        // Method 1: Find the bucket and file path after /storage/v1/object/public/
        const publicIndex = pathParts.findIndex(part => part === 'public');
        
        if (publicIndex !== -1 && publicIndex + 1 < pathParts.length) {
          const bucketName = pathParts[publicIndex + 1];
          // Join the remaining parts and decode URL encoding
          const filePath = pathParts.slice(publicIndex + 2).map(part => decodeURIComponent(part)).join('/');
          
          console.log('Extracted:', { bucketName, filePath });
          
          // Try multiple URL approaches
          const approaches = [];
          
          // Approach 1: Use Supabase client to get public URL
          try {
            const { data: { publicUrl } } = supabase.storage
              .from(bucketName)
              .getPublicUrl(filePath);
            approaches.push({ method: 'supabase-client', url: publicUrl });
          } catch (e) {
            console.warn('Supabase client URL failed:', e);
          }
          
          // Approach 2: Use the original URL as-is
          approaches.push({ method: 'original', url: formattedUrl });
          
          // Approach 3: Construct URL manually with different format
          const baseUrl = url.origin;
          const manualUrl = `${baseUrl}/storage/v1/object/public/${bucketName}/${filePath}`;
          approaches.push({ method: 'manual-construction', url: manualUrl });
          
          // Approach 4: Try signed URL if public doesn't work
          try {
            const { data: signedUrl, error } = await supabase.storage
              .from(bucketName)
              .createSignedUrl(filePath, 3600); // 1 hour expiry
            
            if (!error && signedUrl) {
              approaches.push({ method: 'signed-url', url: signedUrl.signedUrl });
            }
          } catch (e) {
            console.warn('Signed URL approach failed:', e);
          }
          
          console.log('Available URL approaches:', approaches);
          
          // Test each approach and use the first one that works
          for (const approach of approaches) {
            try {
              const testResponse = await fetch(approach.url, { 
                method: 'HEAD',
                mode: 'cors'
              });
              
              if (testResponse.ok) {
                console.log(`✅ Working URL found with ${approach.method}:`, approach.url);
                formattedUrl = approach.url;
                break;
              } else {
                console.warn(`❌ ${approach.method} failed with status:`, testResponse.status);
              }
            } catch (e) {
              console.warn(`❌ ${approach.method} failed with error:`, e.message);
            }
          }
          
          // If no approach worked, use the first available URL
          if (approaches.length > 0) {
            formattedUrl = approaches[0].url;
            console.log('Using first available URL as fallback:', formattedUrl);
          }
        }
      } catch (e) {
        console.error('Error processing URL:', e);
        // Fall back to original URL if processing fails
        formattedUrl = document.url;
      }
    }
    
    // Add cache-busting parameter to prevent caching issues
    const separator = formattedUrl.includes('?') ? '&' : '?';
    const cacheBuster = `${separator}t=${Date.now()}&preview=true`;
    formattedUrl += cacheBuster;
    
    console.log('Final URL for preview:', formattedUrl);
    
    setPreviewDocument({
      ...document,
      url: formattedUrl,
      originalUrl: document.url, // Keep original for download fallback
      isImage: isImage // Store the image detection result
    });
    setShowDocumentPreview(true);
  }

  function closeDocumentPreview() {
    setShowDocumentPreview(false);
    setPreviewDocument(null);
  }



  if (loading) return <Preloader text="Loading applications..." />;

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'super admin')) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            Access denied. You do not have the necessary permissions to view this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Responder Applications</div>
          </div>
          <ProfileMenu />
        </div>
        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />


        <p style={{ color: '#7f8c8d', marginBottom: '24px' }}>
          Review and manage emergency responder applications
        </p>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            {error}
          </div>
        )}



        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          <div style={{ display: 'flex', flex: 1, gap: 16, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by name, email, profession, or organization"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 2, padding: 12, borderRadius: 8, border: '1px solid #ccc' }}
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ flex: 1, minWidth: 150, padding: 12, borderRadius: 8, border: '1px solid #ccc' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            {/* Clear Filters Button */}
            {(search || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearch('');
                  setStatusFilter('all');
                  setPage(1);
                }}
                style={{
                  background: '#f44336',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold'
                }}
                title="Clear all filters"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Applicant
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Contact
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Profession
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Experience
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Status
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Applied
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedApplications.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ padding: 40, textAlign: 'center', color: '#7f8c8d' }}>
                      No applications found
                    </td>
                  </tr>
                ) : (
                  paginatedApplications.map(application => (
                    <tr key={application.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                      <td style={{ padding: '16px 20px' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {application.first_name} {application.last_name}
                          </div>
                          <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                            {application.barangay}, {application.city}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 14, color: '#34495e' }}>
                          {application.email}
                        </div>
                        <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                          {application.mobile_number}
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 14, color: '#34495e' }}>
                          {application.profession}
                        </div>
                        {application.organization && (
                          <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                            {application.organization}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '16px 20px', fontSize: 14, color: '#34495e' }}>
                        {application.experience_years} years
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{
                          background: getStatusColor(application.status),
                          color: '#fff',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {application.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px', fontSize: 14, color: '#7f8c8d' }}>
                        {formatDate(application.created_at)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          <button
                            onClick={() => {
                              setSelectedApplication(application);
                              setAdminNotes('');
                              setShowModal(true);
                            }}
                            style={{
                              background: '#3498db',
                              color: '#fff',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: 12,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          {application.status === 'pending' && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedApplication(application);
                                  setAdminNotes('');
                                  handleApplicationAction(application, 'approved');
                                }}
                                disabled={actionLoading}
                                style={{
                                  background: '#27ae60',
                                  color: '#fff',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: 6,
                                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                                  fontSize: 12,
                                  opacity: actionLoading ? 0.6 : 1
                                }}
                                title="Approve"
                              >
                                <FaCheck />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedApplication(application);
                                  setAdminNotes('');
                                  setShowModal(true);
                                }}
                                disabled={actionLoading}
                                style={{
                                  background: '#e74c3c',
                                  color: '#fff',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: 6,
                                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                                  fontSize: 12,
                                  opacity: actionLoading ? 0.6 : 1
                                }}
                                title="Reject"
                              >
                                <FaXmark />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid #f1f3f4', display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e1e5e9',
                  background: page === 1 ? '#f8f9fa' : '#fff',
                  color: page === 1 ? '#6c757d' : '#495057',
                  borderRadius: 6,
                  cursor: page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <span style={{ padding: '8px 16px', color: '#495057' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e1e5e9',
                  background: page === totalPages ? '#f8f9fa' : '#fff',
                  color: page === totalPages ? '#6c757d' : '#495057',
                  borderRadius: 6,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Application Detail Modal */}
        {showModal && selectedApplication && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              width: '90%',
              maxWidth: 800,
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}>
              {/* Simplified Header */}
              <div style={{
                background: '#08b1c7',
                padding: '20px 24px',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e8eaed'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FaEye style={{ color: '#fff', fontSize: 20 }} />
                  <h2 style={{ 
                    fontWeight: 600, 
                    fontSize: 20, 
                    color: '#fff', 
                    margin: 0
                  }}>
                    Application Details
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedApplication(null);
                    setAdminNotes('');
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    fontSize: 18,
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
                  aria-label="Close"
                >×</button>
              </div>

              {/* Simplified Content */}
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                  <div>
                    <h3 style={{ margin: '0 0 16px 0', color: '#1a1a1a', fontSize: 16, fontWeight: 600 }}>Personal Information</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Name</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.first_name} {selectedApplication.last_name}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Email</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.email}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Mobile</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.mobile_number}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Address</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.address}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Barangay</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.barangay}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>City</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.city}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 style={{ margin: '0 0 16px 0', color: '#1a1a1a', fontSize: 16, fontWeight: 600 }}>Professional Information</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Profession</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.profession}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Organization</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.organization || 'N/A'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Experience</div>
                        <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedApplication.experience_years} years</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Certifications</div>
                        <div style={{ 
                          padding: 8, 
                          background: '#f9fafb', 
                          borderRadius: 6, 
                          fontSize: 14,
                          color: '#1a1a1a',
                          border: '1px solid #e5e7eb'
                        }}>
                          {selectedApplication.certifications || 'None specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedApplication.application_notes && (
                  <div style={{ marginBottom: 24 }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#1a1a1a', fontSize: 16, fontWeight: 600 }}>Application Notes</h3>
                    <div style={{ 
                      padding: 12, 
                      background: '#f9fafb', 
                      borderRadius: 6, 
                      fontSize: 14,
                      color: '#1a1a1a',
                      border: '1px solid #e5e7eb'
                    }}>
                      {selectedApplication.application_notes}
                    </div>
                  </div>
                )}

                {selectedApplication.proof_document_url && (
                  <div style={{ marginBottom: 24 }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#1a1a1a', fontSize: 16, fontWeight: 600 }}>Proof Document</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <FaFileLines style={{ color: '#08b1c7' }} />
                      <span style={{ color: '#6b7280' }}>{selectedApplication.proof_document_type || 'Document'}</span>
                      <button
                        onClick={() => {
                          console.log('=== PROOF DOCUMENT CLICK DEBUG ===');
                          console.log('Proof document URL:', selectedApplication.proof_document_url);
                          console.log('Document type:', selectedApplication.proof_document_type);
                          console.log('Full application object:', selectedApplication);
                          
                          const documentInfo = {
                            url: selectedApplication.proof_document_url,
                            type: selectedApplication.proof_document_type || 'Document',
                            name: `${selectedApplication.first_name} ${selectedApplication.last_name} - Proof Document`
                          };
                          
                          console.log('Document info being passed:', documentInfo);
                          handleDocumentPreview(documentInfo);
                        }}
                        style={{
                          background: '#10b981',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: 'none',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => e.target.style.background = '#059669'}
                        onMouseLeave={e => e.target.style.background = '#10b981'}
                      >
                        <FaEye /> View
                      </button>

                      <a
                        href={selectedApplication.proof_document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: '#08b1c7',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 6,
                          textDecoration: 'none',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          fontWeight: 500
                        }}
                      >
                        <FaDownload /> Download
                      </a>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#1a1a1a', fontSize: 14 }}>
                    Admin Notes
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this application..."
                    style={{
                      width: '100%',
                      minHeight: 80,
                      padding: 12,
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      fontSize: 14,
                      resize: 'vertical',
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={e => e.target.style.borderColor = '#08b1c7'}
                    onBlur={e => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedApplication(null);
                      setAdminNotes('');
                    }}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #d1d5db',
                      background: '#fff',
                      color: '#6b7280',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 500,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => {
                      e.target.style.background = '#f9fafb';
                      e.target.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={e => {
                      e.target.style.background = '#fff';
                      e.target.style.borderColor = '#d1d5db';
                    }}
                  >
                    Cancel
                  </button>
                  {selectedApplication.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApplicationAction(selectedApplication, 'rejected')}
                        disabled={actionLoading}
                        style={{
                          padding: '10px 20px',
                          border: 'none',
                          background: '#dc2626',
                          color: '#fff',
                          borderRadius: 6,
                          cursor: actionLoading ? 'not-allowed' : 'pointer',
                          opacity: actionLoading ? 0.6 : 1,
                          fontSize: 14,
                          fontWeight: 500,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {actionLoading ? 'Processing...' : 'Reject'}
                      </button>
                      <button
                        onClick={() => handleApplicationAction(selectedApplication, 'approved')}
                        disabled={actionLoading}
                        style={{
                          padding: '10px 20px',
                          border: 'none',
                          background: '#08b1c7',
                          color: '#fff',
                          borderRadius: 6,
                          cursor: actionLoading ? 'not-allowed' : 'pointer',
                          opacity: actionLoading ? 0.6 : 1,
                          fontSize: 14,
                          fontWeight: 500,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {actionLoading ? 'Processing...' : 'Approve'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Preview Modal */}
        {showDocumentPreview && previewDocument && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              width: '95%',
              maxWidth: 1200,
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              {/* Document Preview Header */}
              <div style={{
                background: '#08b1c7',
                padding: '16px 24px',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e8eaed'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FaFileLines style={{ color: '#fff', fontSize: 18 }} />
                  <h3 style={{ 
                    fontWeight: 600, 
                    fontSize: 16, 
                    color: '#fff', 
                    margin: 0
                  }}>
                    {previewDocument.name}
                  </h3>
                </div>
                <button
                  onClick={closeDocumentPreview}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    fontSize: 18,
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
                  aria-label="Close"
                >
                  <FaXmark />
                </button>
              </div>

              {/* Document Preview Content */}
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '24px',
                background: '#f8fafc'
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                  minHeight: '60vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {previewDocument.url.toLowerCase().includes('.pdf') ? (
                    <iframe
                      src={previewDocument.url}
                      style={{
                        width: '100%',
                        height: '70vh',
                        border: 'none',
                        borderRadius: 6
                      }}
                      title={previewDocument.name}
                      onError={(e) => {
                        console.error('PDF loading error:', e);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : previewDocument.isImage ? (
                    <div style={{ width: '100%', textAlign: 'center', position: 'relative' }}>
                      <img
                        src={previewDocument.url}
                        alt={previewDocument.name}
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '70vh',
                          objectFit: 'contain',
                          borderRadius: 6
                        }}
                        onLoad={(e) => {
                          console.log('✅ Image loaded successfully:', {
                            src: e.target.src,
                            naturalWidth: e.target.naturalWidth,
                            naturalHeight: e.target.naturalHeight,
                            complete: e.target.complete
                          });
                          // Hide any error message if image loads successfully
                          const errorDiv = e.target.parentNode.querySelector('.error-message');
                          if (errorDiv) errorDiv.style.display = 'none';
                          
                          // Show loading success
                          const loadingDiv = e.target.parentNode.querySelector('.loading-message');
                          if (loadingDiv) loadingDiv.style.display = 'none';
                        }}
                        onError={(e) => {
                          console.error('❌ Image loading error:', {
                            src: e.target.src,
                            error: e,
                            complete: e.target.complete,
                            naturalWidth: e.target.naturalWidth,
                            naturalHeight: e.target.naturalHeight
                          });
                          
                          e.target.style.display = 'none';
                          
                          // Hide loading message
                          const loadingDiv = e.target.parentNode.querySelector('.loading-message');
                          if (loadingDiv) loadingDiv.style.display = 'none';
                          
                          // Show error message
                          const errorDiv = e.target.parentNode.querySelector('.error-message');
                          if (errorDiv) errorDiv.style.display = 'block';
                          
                          // Try alternative URL if available
                          if (previewDocument.originalUrl && e.target.src !== previewDocument.originalUrl) {
                            console.log('🔄 Trying original URL as fallback...');
                            setTimeout(() => {
                              e.target.src = previewDocument.originalUrl;
                              e.target.style.display = 'block';
                            }, 1000);
                          }
                        }}
                        onLoadStart={(e) => {
                          console.log('🔄 Image loading started:', e.target.src);
                          const loadingDiv = e.target.parentNode.querySelector('.loading-message');
                          if (loadingDiv) loadingDiv.style.display = 'block';
                        }}
                      />
                      
                      {/* Loading message */}
                      <div className="loading-message" style={{ 
                        display: 'block',
                        textAlign: 'center',
                        color: '#6b7280',
                        padding: '40px',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ 
                          width: '32px', 
                          height: '32px', 
                          border: '3px solid #e5e7eb',
                          borderTop: '3px solid #08b1c7',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          margin: '0 auto 12px'
                        }}></div>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>
                          Loading image preview...
                        </p>
                      </div>
                      <div className="error-message" style={{ 
                        display: 'none',
                        textAlign: 'center',
                        color: '#6b7280',
                        padding: '40px'
                      }}>
                        <FaFileLines style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                        <h4 style={{ margin: '0 0 8px 0', color: '#374151' }}>Image Could Not Be Loaded</h4>
                        <p style={{ margin: '0 0 16px 0', fontSize: 14 }}>
                          The image file could not be displayed in the preview. This might be due to:
                        </p>
                        <ul style={{ 
                          textAlign: 'left', 
                          margin: '0 0 20px 0', 
                          padding: '0 0 0 20px',
                          fontSize: 14,
                          color: '#6b7280'
                        }}>
                          <li>File permissions or storage access issues</li>
                          <li>File may have been moved or deleted</li>
                          <li>Network connectivity problems</li>
                          <li>File format not supported by browser</li>
                        </ul>
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => {
                              // Try to reload the image with better retry logic
                              const errorDiv = document.querySelector('.error-message');
                              const imgElement = errorDiv.parentNode.querySelector('img');
                              const loadingDiv = errorDiv.parentNode.querySelector('.loading-message');
                              
                              if (imgElement) {
                                // Show loading, hide error
                                errorDiv.style.display = 'none';
                                if (loadingDiv) loadingDiv.style.display = 'block';
                                
                                // Try different URL approaches
                                const retryUrls = [
                                  previewDocument.originalUrl, // Original URL
                                  previewDocument.url.replace(/[?&]t=\d+/, ''), // URL without cache buster
                                  previewDocument.url + '&retry=' + Date.now(), // URL with new cache buster
                                  previewDocument.url.replace('&preview=true', '') // URL without preview flag
                                ].filter(Boolean);
                                
                                let currentRetry = 0;
                                
                                const tryNextUrl = () => {
                                  if (currentRetry >= retryUrls.length) {
                                    // All retries failed
                                    if (loadingDiv) loadingDiv.style.display = 'none';
                                    errorDiv.style.display = 'block';
                                    console.error('All retry attempts failed');
                                    return;
                                  }
                                  
                                  const urlToTry = retryUrls[currentRetry];
                                  console.log(`🔄 Retry attempt ${currentRetry + 1}/${retryUrls.length}: ${urlToTry}`);
                                  
                                  imgElement.onload = () => {
                                    console.log('✅ Retry successful!');
                                    imgElement.style.display = 'block';
                                    if (loadingDiv) loadingDiv.style.display = 'none';
                                  };
                                  
                                  imgElement.onerror = () => {
                                    console.warn(`❌ Retry ${currentRetry + 1} failed`);
                                    currentRetry++;
                                    setTimeout(tryNextUrl, 500); // Wait 500ms before next retry
                                  };
                                  
                                  imgElement.src = urlToTry;
                                };
                                
                                tryNextUrl();
                              }
                            }}
                            style={{
                              background: '#10b981',
                              color: '#fff',
                              padding: '10px 20px',
                              borderRadius: 6,
                              border: 'none',
                              fontSize: 14,
                              fontWeight: 500,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => e.target.style.background = '#059669'}
                            onMouseLeave={e => e.target.style.background = '#10b981'}
                          >
                            🔄 Retry Loading
                          </button>
                          <a
                            href={previewDocument.originalUrl || previewDocument.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: '#08b1c7',
                              color: '#fff',
                              padding: '10px 20px',
                              borderRadius: 6,
                              textDecoration: 'none',
                              fontSize: 14,
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => e.target.style.background = '#0a7a89'}
                            onMouseLeave={e => e.target.style.background = '#08b1c7'}
                          >
                            <FaDownload /> Download Document
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: '#6b7280',
                      padding: '40px'
                    }}>
                      <FaFileLines style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                      <h4 style={{ margin: '0 0 8px 0', color: '#374151' }}>Document Preview Not Available</h4>
                      <p style={{ margin: 0, fontSize: 14 }}>
                        This document type cannot be previewed. Please download to view.
                      </p>
                      <a
                        href={previewDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: '#08b1c7',
                          color: '#fff',
                          padding: '10px 20px',
                          borderRadius: 6,
                          textDecoration: 'none',
                          fontSize: 14,
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          marginTop: 16,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => e.target.style.background = '#0a7a89'}
                        onMouseLeave={e => e.target.style.background = '#08b1c7'}
                      >
                        <FaDownload /> Download Document
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Document Preview Footer */}
              <div style={{
                background: '#fff',
                padding: '16px 24px',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                borderTop: '1px solid #e8eaed'
              }}>
                <button
                  onClick={closeDocumentPreview}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    color: '#6b7280',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={e => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  Close
                </button>
                <a
                  href={previewDocument.originalUrl || previewDocument.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: '#08b1c7',
                    color: '#fff',
                    padding: '10px 20px',
                    borderRadius: 6,
                    textDecoration: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => e.target.style.background = '#0a7a89'}
                  onMouseLeave={e => e.target.style.background = '#08b1c7'}
                >
                  <FaDownload /> Download
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResponderApplications;